import React from "react";
import { Button, Tag } from "../../../../../design-system";
import mediaOps from "../../../../../database/mediaOps/mediaOpsApi";
import { useOpRead } from "./hooks";
import { severityVariant } from "./stateLabels";
import { Grow, ListRow, Panel, PanelHead, ResultNotice, Row, Small, fmtTime } from "./ui";

// Operations health as the server reports it: scheduler heartbeats, backlog,
// stale leases, unknown or failed deliveries, missing owners and send flags
// that are off. Items are listed most severe first.

type HealthItem = { kind: string; severity: string; message: string; accountId?: string };

const FLAG_LABEL: Record<string, string> = {
  ownerEmailSendEnabled: "Owner email sending",
  tiktokInboxEnabled: "TikTok inbox delivery",
  mediaDeliveryEnabled: "Delivery dispatcher",
  mediaJobsEnabled: "Job runner",
  tiktokVideoInbox: "TikTok video inbox",
};

export const HealthView: React.FC<{ communityId: string }> = ({ communityId }) => {
  const read = useOpRead(() => mediaOps.health(communityId), [communityId]);
  const data = read.result?.data || {};
  const items: HealthItem[] = data.items || [];
  const flags: Record<string, boolean> = data.flags || {};
  const overall = String(data.status || "unknown");

  return (
    <Panel>
      <PanelHead>
        Health
        <Tag size="sm" variant={overall === "ok" ? "success" : overall === "critical" ? "danger" : "warning"}>
          {overall}
        </Tag>
        <Grow />
        <Button size="sm" variant="secondary" onClick={read.reload}>
          Refresh
        </Button>
      </PanelHead>
      {read.result?.status === "blocked" ? <ResultNotice result={read.result} /> : null}
      {data.checkedAt ? <Small>Checked {fmtTime(data.checkedAt)}</Small> : null}
      {Object.keys(flags).length > 0 ? (
        <Row>
          {Object.keys(flags).map((k) => (
            <Tag key={k} size="sm" variant={flags[k] ? "success" : "warning"}>
              {FLAG_LABEL[k] || k} is {flags[k] ? "on" : "off"}
            </Tag>
          ))}
        </Row>
      ) : null}
      {data.backlog ? (
        <Small>
          Due targets {data.backlog.dueTargets ?? 0}. Oldest due {data.backlog.oldestDueAt ? fmtTime(data.backlog.oldestDueAt) : "none"}.
        </Small>
      ) : null}
      {items.length === 0 && read.result && read.result.status !== "blocked" ? <Small>No issues reported.</Small> : null}
      {items.map((it, i) => (
        <ListRow key={`${it.kind}-${i}`}>
          <Tag size="sm" variant={severityVariant(it.severity)}>
            {it.severity}
          </Tag>
          <Small>{it.kind.replace(/_/g, " ")}</Small>
          <span style={{ fontFamily: "var(--font-sans)", fontSize: 13 }}>{it.message}</span>
        </ListRow>
      ))}
    </Panel>
  );
};

export default HealthView;
